"use client";

import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/utils/AppConfig";

type PayoutData = {
  key: string;
  amount: number;
};

interface PayoutsChartProps {
  data: PayoutData[];
}

export function PayoutsChart({ data }: PayoutsChartProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const label = (
    <span dir={lang === "ar" ? "rtl" : "ltr"} className="flex items-center gap-1">
      {t("PayoutsChart.Label")} {AppConfig.currency.icon("white", 10)}
    </span>
  );

  const payoutsConfig = {
    amount: {
      label: label,
      color: "#3CB371", // green
    },
  } satisfies ChartConfig;

  const chartColor = payoutsConfig.amount.color;

  return (
    <Card className="force-light-mode border rounded-lg shadow-lg bg-white">
      <CardHeader>
        <CardTitle className="text-xl font-semibold text-gray-800">
          {t("PayoutsChart.Title")}
        </CardTitle>
      </CardHeader>

      <CardContent>
        <ChartContainer
          config={payoutsConfig}
          className="h-[500px] w-full bg-gray-100 rounded-lg py-6 shadow-inner"
        >
          <AreaChart
            data={data}
            margin={{ left: 24, right: 12, top: 8, bottom: 0 }}
          >
            <CartesianGrid vertical={false} horizontal={false} />
            <XAxis
              dataKey="key"
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              tick={{ fontSize: 12, fill: "#4A5568" }}
              tickFormatter={(value) => value.slice(0, 4)}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              tick={{ fontSize: 12, fill: "#4A5568" }}
              
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent indicator="line" />}
            />
            <Area
              dataKey="amount"
              type="monotone"
              fill={chartColor}
              fillOpacity={0.4}
              stroke={chartColor}
              strokeWidth={2}
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
